// deps
import React, { ReactElement, useCallback } from "react";

// internal-deps
import { RouteProps, GenericError, CoreLayout, Storage, useEffectOnce } from "@swiggy-private/core-ui";
import {
    DineoutCheckoutRoute,
    SCENES_FULL_PAYMENT_FLOW_KEY,
    SCENES_FULL_PAYMENT_ORDER_ID_KEY,
    DineoutCartType,
} from "@swiggy-private/checkout-core";
import { BOOLEAN_AS_STRING } from "@swiggy-private/payment-types";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";

// hooks
import useFetchDineoutCart from "@dineout-cart-client/hooks/useFetchDineoutCart";

// constants
import {
    CUSTOM_ERROR_PAYLOAD,
    RETRY,
    EMPTY_STATE_HEADER,
    OOPS_SOMETHING_WENT_WRONG,
} from "@dineout-cart-client/constants";

// components
import Header from "../table-reservation/components/header/Header";

// styles
import { ErrorContentWrapper, ScenesFullPaymentContainer } from "./styles";

export function ScenesFullPayment(props: RouteProps<DineoutCheckoutRoute, { orderId?: string }>): ReactElement | null {
    // Store orderId in session storage at component mount if available
    useEffectOnce(() => {
        if (props.data?.orderId) {
            Storage.setItem(SCENES_FULL_PAYMENT_ORDER_ID_KEY, props.data.orderId);
            // Set session storage flag for GPO to identify scenes flow
            Storage.setItem(SCENES_FULL_PAYMENT_FLOW_KEY, BOOLEAN_AS_STRING.TRUE);
        }
    });
    const { error, loading: isLoading } = useFetchDineoutCart(DineoutCartType.SCENES_FULL_PAYMENT);

    const reloadCart = useCallback(() => {
        window.location.reload();
    }, []);

    // Show error if present
    if (error) {
        return (
            <CoreLayout>
                <CoreLayout.Header isDineoutCartHeader>
                    <Header restaurantDetails={EMPTY_STATE_HEADER} isEventsBookingFlow={false} />
                </CoreLayout.Header>
                <CoreLayout.Content>
                    <ErrorContentWrapper>
                        <GenericError
                            action={{
                                onClick: reloadCart,
                                text: RETRY,
                            }}
                            title={CUSTOM_ERROR_PAYLOAD.TITLE}
                            message={error?.errorPayload?.statusMessage || OOPS_SOMETHING_WENT_WRONG}
                            imageId={CUSTOM_ERROR_PAYLOAD.IMAGE}
                        />
                    </ErrorContentWrapper>
                </CoreLayout.Content>
            </CoreLayout>
        );
    }

    // Show loader only when loading (during API call)
    if (isLoading) {
        return (
            <ScenesFullPaymentContainer>
                <Loader
                    size="large"
                    type="secondary"
                    foregroundColor="positive"
                    backgroundColor="positive_Sub"
                    isDisabled={false}
                />
            </ScenesFullPaymentContainer>
        );
    }

    // Return null when not loading and no error (not a terminal state)
    return null;
}
